import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PlusIcon, UserIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Manager, useCreateManager, useUpdateManager } from '@/api/managers'
import { AvatarFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import { useDirtyFormGuard } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../shared/form-changes'
import { getPersonAvatarSrc } from '../../shared/person-avatar'
import { PersonFormLayout } from '../../shared/person-form-layout'
import {
  MANAGER_NAME_MAX_LENGTH,
  ManagerFormData,
  ManagerFormMode,
  makeManagerFormSchema,
} from './manager-form-modal.constants'

interface ManagerFormModalProps {
  manager?: Manager
  mode: ManagerFormMode
  onClose: VoidFunction
}

export const ManagerFormModal = ({ manager, mode, onClose }: ManagerFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: createManager } = useCreateManager()
  const { mutateAsync: updateManager } = useUpdateManager()

  const isEdit = mode === 'edit'

  const schema = useMemo(() => makeManagerFormSchema(t, mode), [t, mode])

  const existingAvatarUrl = isEdit && manager ? getPersonAvatarSrc(manager.user.avatar) : undefined

  const form = useForm<ManagerFormData>({
    defaultValues: {
      avatar: [],
      email: manager?.user.email ?? '',
      firstName: manager?.user.firstName ?? '',
      lastName: manager?.user.lastName ?? '',
    },
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState

  const changeFields = useMemo<FormChangeFields<ManagerFormData>>(
    () => ({
      avatar: { image: true, label: t('organisation:managers.form.avatar.label') },
      firstName: { label: t('organisation:managers.form.firstName.label') },
      lastName: { label: t('organisation:managers.form.lastName.label') },
      ...(isEdit ? {} : { email: { label: t('organisation:managers.form.email.label') } }),
    }),
    [t, isEdit],
  )

  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<ManagerFormData> = async (data) => {
    const avatar = data.avatar?.[0]
    const firstName = data.firstName.trim()
    const lastName = data.lastName.trim()

    try {
      if (isEdit && manager) {
        await updateManager({ avatar, firstName, id: manager.id, lastName })
        toastFactory.success(t('organisation:managers.form.toasts.updated'))
      } else {
        await createManager({ avatar, email: data.email.trim(), firstName, lastName })
        toastFactory.success(t('organisation:managers.form.toasts.created'))
      }
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const submit = form.handleSubmit(onSubmit)
  const submitDisabled = isEdit && !isDirty

  return (
    <Modal
      autoWidth
      title={
        isEdit
          ? t('organisation:managers.form.titleEdit')
          : t('organisation:managers.form.titleNew')
      }
      headerIcon={UserIcon}
      footer={
        <>
          <Button use="secondary" onClick={handleClose}>
            {isEdit ? t('common:actions.cancel') : t('common:actions.back')}
          </Button>
          <Button disabled={submitDisabled} loading={isSubmitting} use="primary" onClick={submit}>
            {isEdit ? t('common:actions.save') : t('common:actions.add')}
            <Icon icon={isEdit ? CheckIcon : PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout submitDisabled={submitDisabled} form={form} onSubmit={submit}>
        <AvatarFormField
          existingImageUrl={existingAvatarUrl}
          label={t('organisation:managers.form.avatar.label')}
          uploadLabel={t('organisation:managers.form.avatar.uploadLabel')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:managers.form.firstName.label')}
            maxLength={MANAGER_NAME_MAX_LENGTH}
            name="firstName"
            placeholder={t('organisation:managers.form.firstName.placeholder')}
          />
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:managers.form.lastName.label')}
            maxLength={MANAGER_NAME_MAX_LENGTH}
            name="lastName"
            placeholder={t('organisation:managers.form.lastName.placeholder')}
          />
        </Grid>

        <TextInputFormField
          withReservedFooter
          required
          disabled={isEdit}
          label={t('organisation:managers.form.email.label')}
          name="email"
          placeholder={t('organisation:managers.form.email.placeholder')}
        />

        {!isEdit && (
          <Typography color="black500" size="text-small">
            {t('organisation:managers.form.setupHint')}
          </Typography>
        )}
      </PersonFormLayout>
    </Modal>
  )
}
